// START IMPORTS
// Context and configuration imports
import { AppConfig } from '@ltvco/refresh-lib/ctx';
// Theme and UI imports
import { useReport } from '@ltvco/refresh-lib/hooks';
import { Box, Stack } from '@ltvco/refresh-lib/theme';
// Utility imports
import {
  DateUtil,
  formatPhone,
  isNeighborSpoofed,
  isZeroed,
  unformatPhone,
  useScrollToSectionOnNavigate,
  usePhoneFormatter,
} from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import {
  useSession,
  usePhoneSearchData,
  ReportLoading,
  type ReportOptions,
  ReportFactory,
  type commonTypes,
  type ReportMonitor,
  ReportChangesOverview,
  DebugMenu,
} from '@ltvco/refresh-lib/v1';
// Core lib v2 imports
import {
  AddressHistorySection,
  AssetsSection,
  EducationSection,
  EmailSection,
  JobsSection,
  NotesSection,
  PhoneSection,
  PotentialOwners,
  RelatedReportsSection,
  ReportActionsWithDateUpdated,
  ReportNavigationMenu,
  type ReportNavigationMenuProps,
  ReportNavigationMobileMenu,
  ReportNullState,
  ReportRating,
  SocialSection,
  PhoneReportOverview,
  ReportClaimingSection,
  MonitoringCardV2,
  MonitoringBannerSection,
  CommentsSection,
  FraudPhoneThreatDetection,
  usePhoneComments,
  ReportAttachmentsCTA,
  ReportAttachmentsSection,
} from '@ltvco/refresh-lib/v2';
// Vendor imports
import { useQueryClient, useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// App-specific imports
import { constants } from 'appConstants';
import {
  getPhoneNavLinkData,
  getPhoneNavLinkDataV2,
} from 'navLinkData/phoneNavLinkData';

// Type imports
import type { ReportRouteProps } from './types';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface PhoneReportProps extends ReportRouteProps {}
// END INTERFACES_AND_TYPES

// START CONSTANTS
const reportType = 'phone';
// END CONSTANTS

export function PhoneReportPage({
  permalink,
  isMonitored: _isMonitored = false,
}: PhoneReportProps) {
  // START HOOKS_AND_QUERIES
  const {
    session: { account },
  } = useSession();
  const { logError } = useContext(AppConfig);
  const queryClient = useQueryClient();

  const queryResult = useReport(permalink);
  const { currentMonitors, reportIsMonitored } = queryResult;

  const { hash, search } = useLocation();

  const currentUserInfo = account?.account?.user_info;

  const rawPhone = queryResult?.data?.meta?.search_data?.phone_number ?? '';
  const formattedPhoneNumber = usePhoneFormatter(rawPhone, true);

  const { commentsQuery } = usePhoneComments({
    permalink,
    phoneNumber: rawPhone,
    logError,
    enabled: queryResult.isSuccess,
  });

  const zeroed = isZeroed(queryResult);
  useScrollToSectionOnNavigate(hash, queryResult);

  const { data: phoneSearchData } = usePhoneSearchData(
    logError,
    queryResult?.data?.meta?.search_data?.phone_number
  );
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [reportOptions, setReportOptions] = useState<ReportOptions>({
    potentialOwnerIndex: 0,
    showHighConfidenceDataOnly: false,
    higherConfidenceThreshold: constants.config.higherConfidenceThreshold,
  });
  const [activeClaiming, setActiveClaiming] = useState(false);
  const [spam, setSpam] = useState<'unsure' | 'yes' | 'no'>('unsure');
  // END STATE_MANAGEMENT

  // START EVENT_HANDLERS
  const handleSetReportOptions = (newOptions: Partial<ReportOptions>) => {
    setReportOptions({ ...reportOptions, ...newOptions });
  };
  // END EVENT_HANDLERS

  // START EFFECTS
  useEffect(() => {
    setActiveClaiming(
      currentMonitors?.some((monitor: ReportMonitor) => {
        return monitor.permalink === permalink && monitor.is_claimed;
      })
    );
  }, [currentMonitors, permalink]);

  useEffect(() => {
    if (queryResult.isSuccess && queryResult.data) {
      queryClient.invalidateQueries(['request', 'bbq']);
    }
  }, [queryResult.isSuccess, queryResult.data, reportOptions]);
  // END EFFECTS

  // START EARLY_RETURNS
  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={15} />;
  }
  if (zeroed) {
    return <ReportNullState />;
  }

  const report = ReportFactory.create(queryResult?.data, 'phone');

  if (!report) {
    return <ReportLoading menuItems={15} />;
  }

  const businesses =
    report?.data?.businesses ??
    report?.data?.rawData?.entities?.businesses ??
    [];
  const businessName = businesses[0]?.names?.[0]?.name ?? '';

  if (
    (!report?.data?.people || !report?.data?.people?.length) &&
    businesses.length === 0
  ) {
    return <ReportNullState />;
  }
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const showReportClaiming =
    unformatPhone(currentUserInfo?.phone_number || '') === report.phone;

  const ClaimingComponent = showReportClaiming ? (
    <ReportClaimingSection
      reportPermaLink={permalink}
      reportType={reportType}
      reportIsMonitoredAndClaimed={activeClaiming}
    />
  ) : reportIsMonitored ? null : (
    <MonitoringBannerSection
      reportPermaLink={permalink}
      reportType={reportType}
    />
  );

  const MonitoringComponent = (
    <MonitoringCardV2 permalink={permalink} reportType={reportType} />
  );

  const neighborSpoofed = isNeighborSpoofed(
    report.data,
    account?.account?.user_info.phone_number
  );

  const isBusinessSelected =
    reportOptions.potentialOwnerIndex < businesses.length;
  const selectedBusinessName = isBusinessSelected
    ? businesses[reportOptions.potentialOwnerIndex]?.names?.[0]?.name ?? ''
    : '';

  const personOwnerOptions = {
    ...reportOptions,
    potentialOwnerIndex: isBusinessSelected
      ? 0
      : reportOptions.potentialOwnerIndex - businesses.length,
  };
  const owner = report.getOwner(personOwnerOptions);

  const {
    ownerName,
    emails,
    educations,
    jobs,
    profiles,
    usernames,
    phones,
    addresses,
    ownedAssets,
    relatives,
  } = owner;

  const {
    risk,
    connectivity,
    location: supportingLocation,
  } = report.supportingPhone || {};

  const {
    fraud_score,
    is_risky,
    is_recent_abuse,
    is_spammer,
    is_do_not_call,
    is_voip,
    is_prepaid,
    is_leaked,
  } = risk || {};

  const { active_status, user_activity, is_active } = connectivity || {};

  const queryParams = new URLSearchParams(search);
  const fallbackPhone = queryParams.get('phone') || '';
  const phoneNumber = formatPhone(report.phone) ?? formatPhone(fallbackPhone);

  const otherPhones = phones.filter(
    (phone: commonTypes.Phone) => phone.number !== fallbackPhone
  );

  const selectedOwnerDisplayName = isBusinessSelected
    ? selectedBusinessName
    : ownerName || formattedPhoneNumber;

  let potentialOwnersNumber =
    businesses.length + (report.data.people?.length ?? 0);
  if (potentialOwnersNumber === 0 && businessName) {
    potentialOwnersNumber = 1;
  }

  const people = report.data.people ?? [];
  const businessNames = businesses
    .map((b) => (b.names?.[0]?.name ? `${b.names[0].name} (Business)` : ''))
    .filter(Boolean);
  const personNames = people.map((person) => {
    const first = person?.identity?.names?.[0]?.parsed?.first ?? '';
    const last = person?.identity?.names?.[0]?.parsed?.last ?? '';
    return `${first} ${last}`.trim() || 'Unknown';
  });
  const potentialOwnersNames = [...businessNames, ...personNames];

  const { main_line_type, num_searches, spam_score } = report.data;
  const searchCount = phoneSearchData?.total_volume || num_searches || 0;
  const internationalPhoneNumber = (rawPhone as string).startsWith('+');
  const supportingPhone = queryResult?.data?.supporting?.phones?.[0];
  const fallbackInfo = internationalPhoneNumber
    ? {
        address: supportingPhone?.location?.address || undefined,
        phone: formattedPhoneNumber || undefined,
      }
    : undefined;

  const phoneNavLinkDataV2 = getPhoneNavLinkDataV2(owner, {
    potentialOwnersLength: potentialOwnersNumber,
    searchVolumeLength: searchCount > 0 ? 1 : 0,
    commentsLength: (commentsQuery.data?.comment_count as number) ?? 0,
    neighborSpoofingLength: neighborSpoofed ? 1 : 0,
    spamReportsCount: report.data.spam_reports_count ?? 0,
  });

  const phoneNavLinkData = getPhoneNavLinkData(owner, {
    potentialOwnersLength: potentialOwnersNumber,
    searchVolumeLength: searchCount > 0 ? 1 : 0,
    commentsLength: (commentsQuery.data?.comment_count as number) ?? 0,
    neighborSpoofingLength: neighborSpoofed ? 1 : 0,
    spamReportsCount: report.data.spam_reports_count ?? 0,
  });

  const comments = commentsQuery.data?.comments ?? [];
  const carrier = report.data.meta?.search_data?.carrier;
  const date = new DateUtil();
  const reportUpdateDate = report.data.meta?.updated_at
    ? date.parseDateFromString(
        report.data.meta?.updated_at,
        'yyyy-MM-dd',
        'yyyy-MM-dd HH:mm:ss ZZZ'
      )
    : '';

  document.title = `${formatPhone(phoneNumber)} - PeopleLooker`;
  const isPhonePartialAddress = useFeatureIsOn('OAR-2083');
  const alternativeTitle = isPhonePartialAddress
    ? 'Possible Locations'
    : undefined;
  // END DATA_PREPARATION

  // START RENDER
  return (
    <Box>
      <Stack direction="row">
        {/* START DESKTOP_NAVIGATION */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
          }}
        >
          <ReportNavigationMenu
            reportType="Phone Report"
            headerTitle={formatPhone(phoneNumber)}
            menuItems={
              phoneNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
            }
          />
        </Box>
        {/* END DESKTOP_NAVIGATION */}

        {/* START MAIN_CONTENT */}
        <Box marginTop={0}>
          {/* START REPORT_CHANGES_OVERVIEW */}
          {reportIsMonitored && (
            <ReportChangesOverview
              permalink={permalink}
              navLinkData={phoneNavLinkData}
            />
          )}
          {/* END REPORT_CHANGES_OVERVIEW */}

          {/* START REPORT_ACTIONS */}
          <Box
            sx={{
              height: 30,
              backgroundColor: 'background.default',
              position: 'sticky',
              marginBottom: '-10px',
              marginX: -1,
              top: 52,
              zIndex: 10,
              display: {
                xs: 'none',
                sm: 'block',
                md: 'block',
                lg: 'block',
              },
            }}
          />

          <ReportActionsWithDateUpdated
            reportType={reportType}
            reportTitle="Phone"
            dateUpdated={reportUpdateDate}
            reportUpgraded={false}
          />
          {/* END REPORT_ACTIONS */}

          {/* START REPORT_OVERVIEW */}
          <PhoneReportOverview
            dateUpdated={reportUpdateDate}
            reportType={reportType}
            phone={formatPhone(phoneNumber)}
            personName={selectedOwnerDisplayName}
            neighborSpoofed={neighborSpoofed}
            Monitoring={MonitoringComponent}
            phoneOverviewItemsData={{
              comments: commentsQuery.data?.comments ?? [],
              mainLineType: main_line_type,
              numSearches: searchCount,
              spamScore: spam_score,
              carrier,
              peopleCount: potentialOwnersNumber,
              fraudScore: fraud_score ?? 0,
              isRisky: is_risky,
              isDoNotCall: is_do_not_call ?? false,
              isVOIP: is_voip ?? false,
              isPrepaid: is_prepaid ?? false,
              isValid: report.supportingPhone?.is_valid ?? false,
              isActive: is_active ?? false,
              isLeaked: is_leaked ?? false,
              isSpammer: is_spammer ?? false,
              isRecentAbuse: is_recent_abuse ?? false,
              activeStatus: active_status ?? 'Unknown',
              userActivity: user_activity ?? 'Unknown',
              timezone: supportingLocation?.timezone ?? '',
              potentialOwners: potentialOwnersNames,
              parsedLocation: supportingLocation?.address?.parsed,
              location: supportingLocation?.address?.full ?? '',
            }}
            showHighConfidenceToggle={false}
            showHighConfidenceDataOnly={
              reportOptions.showHighConfidenceDataOnly
            }
            setShowHighConfidenceDataOnly={(value: boolean) => {
              handleSetReportOptions({ showHighConfidenceDataOnly: value });
            }}
            useThreatDetectionSection
            internationalPhoneNumber={internationalPhoneNumber}
            potentialOwner={
              !isBusinessSelected
                ? people[reportOptions.potentialOwnerIndex - businesses.length]
                : undefined
            }
            fallbackInfo={fallbackInfo}
          />
          {/* END REPORT_OVERVIEW */}

          <ReportAttachmentsCTA
            userCode={currentUserInfo?.user_code || ''}
            permalink={permalink}
            reportType={reportType}
          />

          {ClaimingComponent}

          <PotentialOwners
            potentialOwners={people}
            potentialOwnerIndex={reportOptions.potentialOwnerIndex}
            setPotentialOwnerIndex={(index: number) => {
              handleSetReportOptions({ potentialOwnerIndex: index });
            }}
            personName={selectedOwnerDisplayName}
            permalink={permalink}
            businesses={businesses}
            reportType={reportType}
          />
          {/* END POTENTIAL_OWNERS */}

          {/* START SECTIONS */}
          <PhoneSection
            personName={selectedOwnerDisplayName}
            phoneList={otherPhones}
            permalink={permalink}
            alternateTitle="Other Phone Numbers"
          />
          <EmailSection
            personName={selectedOwnerDisplayName}
            emailList={emails}
            permalink={permalink}
          />
          <AddressHistorySection
            addresses={addresses}
            personName={selectedOwnerDisplayName}
            permalink={permalink}
            alternateTitle={alternativeTitle}
          />
          <SocialSection
            personName={selectedOwnerDisplayName}
            profiles={profiles}
            usernames={usernames}
            permalink={permalink}
          />
          <JobsSection
            personName={selectedOwnerDisplayName}
            jobs={jobs}
            permalink={permalink}
          />

          <EducationSection
            personName={selectedOwnerDisplayName}
            educations={educations}
            permalink={permalink}
          />
          <AssetsSection
            personName={selectedOwnerDisplayName}
            ownedAssets={ownedAssets}
            permalink={permalink}
          />

          <FraudPhoneThreatDetection
            isOnPhoneReport
            fraudScore={fraud_score ?? 0}
            isRisky={is_risky}
            isRecentAbuse={is_recent_abuse}
            isSpammer={is_spammer}
            searchCount={searchCount}
            spamScore={report?.data?.spam_score}
            spamReportsCount={report?.data?.spam_reports_count}
            userComments={comments?.length}
            permalink={permalink}
            spam={spam}
            setSpam={setSpam}
          />

          <CommentsSection
            comments={commentsQuery.data?.comments ?? []}
            reportData={report.data}
            permalink={permalink}
            formattedPhoneNumber={formattedPhoneNumber}
          />

          <ReportAttachmentsSection
            userCode={currentUserInfo?.user_code || ''}
            permalink={permalink}
            reportType={reportType}
          />

          <NotesSection permalink={permalink} />

          <RelatedReportsSection
            reportType="phone"
            personName={selectedOwnerDisplayName}
            potentialOwners={people}
            relatives={relatives}
            addresses={addresses}
            phones={phones}
            emails={emails}
            usernames={usernames}
          />
          <ReportRating
            rating={report.data.meta?.rating || null}
            report_type={reportType}
          />
          {/* END SECTIONS */}
        </Box>
        {/* END MAIN_CONTENT */}
      </Stack>

      {/* START MOBILE_NAVIGATION */}
      <ReportNavigationMobileMenu
        reportType="Phone Report"
        headerTitle={formatPhone(phoneNumber)}
        menuItems={phoneNavLinkDataV2 as ReportNavigationMenuProps['menuItems']}
        isMobileBannerShowed={false}
      />
      {/* END MOBILE_NAVIGATION */}

      {/* START DEBUG_MENU */}
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
      {/* END DEBUG_MENU */}
    </Box>
  );
  // END RENDER
}
